import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

/**
 * Shared pieces for the public marketing pages.
 *
 * The house rules still apply out here (FE-1): paper neutrals, borders rather
 * than shadows, graphite ink for anything primary, and sapphire kept for
 * *state* — a marketing page that invents its own blue button is a marketing
 * page that stops looking like the product it is selling.
 */
export const CONTACT_EMAIL = 'distrx.io@gmail.com';
export const EASE = [0.2, 0.7, 0.2, 1];

/** Every infinite loop on these pages is gated on this. */
export const REDUCE =
  typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export const fadeUp = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.5, ease: EASE },
};

export const heroIn = (delay = 0) => ({
  initial: { opacity: 0, y: 22 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, delay, ease: EASE },
});

/** A section label. Small, quiet, and the same on every section. */
export function Kicker({ children, center = false, light = false }) {
  return (
    <div className={`flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.14em] ${center ? 'justify-center' : ''}`}>
      <span aria-hidden="true" className="h-px w-5" style={{ background: light ? 'rgba(255,255,255,0.35)' : 'rgb(var(--line))' }} />
      <span style={{ color: light ? 'rgba(255,255,255,0.6)' : 'rgb(var(--faint))' }}>{children}</span>
    </div>
  );
}

export function Section({ id, children, className = '', tone = 'default' }) {
  return (
    <section
      id={id}
      className={`px-4 py-20 sm:px-6 sm:py-24 ${className}`}
      style={tone === 'sunken' ? { background: 'rgb(var(--surface-2))' } : undefined}
    >
      <div className="mx-auto w-full max-w-6xl">{children}</div>
    </section>
  );
}

export function SectionHead({ kicker, title, blurb, center = false, light = false }) {
  return (
    <motion.div {...fadeUp} className={`max-w-2xl ${center ? 'mx-auto text-center' : ''}`}>
      {kicker && <Kicker center={center} light={light}>{kicker}</Kicker>}
      <h2
        className="mt-4 text-[26px] font-semibold leading-[1.15] tracking-tight sm:text-[34px]"
        style={{ color: light ? '#fff' : 'rgb(var(--ink))' }}
      >
        {title}
      </h2>
      {blurb && (
        <p
          className="mt-3 text-[15px] leading-relaxed"
          style={{ color: light ? 'rgba(255,255,255,0.68)' : 'rgb(var(--ink-soft))' }}
        >
          {blurb}
        </p>
      )}
    </motion.div>
  );
}

/** A bordered panel. The whole page is built out of these. */
export function Panel({ children, className = '', hover = false }) {
  return (
    <div
      className={`rounded-2xl border ${hover ? 'mk-hover' : ''} ${className}`}
      style={{ background: 'rgb(var(--surface))', borderColor: 'rgb(var(--line))' }}
    >
      {children}
    </div>
  );
}

/** Primary call to action: graphite ink, like every primary button in the app. */
export function PrimaryLink({ to, href, className = '', onClick, children }) {
  const cls = `btn btn-primary inline-flex items-center justify-center gap-2 ${className}`;
  return to
    ? <Link to={to} className={cls} onClick={onClick}>{children}</Link>
    : <a href={href} className={cls} onClick={onClick}>{children}</a>;
}

export function GhostLink({ to, href, className = '', onClick, children }) {
  const cls = `btn btn-ghost inline-flex items-center justify-center gap-2 ${className}`;
  return to
    ? <Link to={to} className={cls} onClick={onClick}>{children}</Link>
    : <a href={href} className={cls} onClick={onClick}>{children}</a>;
}

/** A slow drift. The only ambient motion on the page, and it can be turned off. */
export function Float({ children, className = '', duration = 7, delay = 0, dy = 8 }) {
  if (REDUCE) return <div className={className}>{children}</div>;
  return (
    <motion.div
      className={className}
      animate={{ y: [0, -dy, 0] }}
      transition={{ repeat: Infinity, duration, delay, ease: 'easeInOut' }}
    >
      {children}
    </motion.div>
  );
}
